import React from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, Crown, Star, Medal, CheckCircle, Megaphone } from "@phosphor-icons/react";
import Seo from "../components/Seo";

const TIERS = [
  {
    id: "platinum",
    label: "Platinum",
    icon: Crown,
    accent: "border-cyber-yellow text-cyber-yellow",
    badge: "bg-cyber-yellow text-cyber-black",
    seats: "10 attendee passes",
    pitch: "Headline-tier presence. Top of every list, every email, every sign.",
    bullets: [
      "Top-of-page logo placement on event hub & confirmation emails",
      "Dedicated 60-second mic moment on the floor (optional, no panel)",
      "10 invitations for your team — pre-approved, no review queue",
      "First-look access to attendee directory after the event (opt-in only)",
      "Branded co-host of one networking station / lounge corner",
      "Full-event photo package featuring your booth/team",
    ],
    cta: "Request Platinum brief",
    type: "sponsorship",
  },
  {
    id: "gold",
    label: "Gold",
    icon: Star,
    accent: "border-white/70 text-white",
    badge: "bg-white text-cyber-black",
    seats: "5 attendee passes",
    pitch: "High-visibility presence with the right room density to actually book meetings.",
    bullets: [
      "Mid-tier logo placement on hub, signage, and approved-RSVP email",
      "5 invitations for your team — pre-approved",
      "Branded element at one networking touchpoint (bar / coffee bar / step-and-repeat)",
      "Pre-event mention in the FintechOmni newsroom",
      "Post-event group photo package",
    ],
    cta: "Request Gold brief",
    type: "sponsorship",
  },
  {
    id: "silver",
    label: "Silver",
    icon: Medal,
    accent: "border-cyber-green text-cyber-green",
    badge: "bg-cyber-green text-cyber-black",
    seats: "2 attendee passes",
    pitch: "Get into the room, get on the page. Right-fit entry tier for first-time partners.",
    bullets: [
      "Logo on the public sponsor wall + event hub",
      "2 invitations for your team — pre-approved",
      "Inclusion in pre-event sponsor announcement",
      "Standard newsroom mention",
    ],
    cta: "Request Silver brief",
    type: "sponsorship",
  },
];

const Tile = ({ k, v }) => (
  <div className="hard-border bg-cyber-surface p-5">
    <div className="term-label">{k}</div>
    <div className="font-display font-black text-2xl mt-2 leading-tight">{v}</div>
  </div>
);

const Sponsor = () => {
  return (
    <main className="grain min-h-screen" data-testid="sponsor-page">
      <Seo
        path="/sponsor"
        title="Sponsor FintechOmni — Platinum, Gold & Silver tiers"
        description="Sponsorship tiers for FintechOmni private events. Reach senior fintech marketers across our six events / year. Platinum, Gold, Silver tiers — request the brief in 24 hours."
        keywords="fintech sponsorship, sponsor fintech event, fintech marketing sponsor, BNPL sponsor, fintech vendor sponsor, sponsor private event, fintech CMO sponsor, sponsor las vegas fintech"
        noindex
      />

      {/* HERO */}
      <section className="border-b border-cyber-line">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 pt-12 sm:pt-20 pb-14 sm:pb-20">
          <div className="term-label mb-4 sm:mb-5">/ sponsor</div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 lg:gap-10 items-end">
            <h1 className="lg:col-span-8 font-display font-black tracking-tighter text-[44px] sm:text-7xl lg:text-[100px] leading-[0.95] sm:leading-[0.92]">
              Sponsor the room, <br />
              <span className="text-cyber-yellow">not the panel.</span>
            </h1>
            <p className="lg:col-span-4 text-white/75 text-base sm:text-lg leading-relaxed">
              Our events are sub-20% approval, decision-makers only. Three tiers, real ROI receipts, no death-by-PowerPoint.
              Tell us roughly what you're trying to achieve and we'll send the deck within 24 hours.
            </p>
          </div>

          <div className="mt-12 grid grid-cols-2 md:grid-cols-4 gap-3">
            <Tile k="/ approval rate" v="< 20%" />
            <Tile k="/ events / year" v="6" />
            <Tile k="/ avg room" v="200" />
            <Tile k="/ audience" v="Decision-makers" />
          </div>
        </div>
      </section>

      {/* WHO YOU'RE ACTUALLY REACHING */}
      <section className="border-b border-cyber-line bg-cyber-black">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="term-label mb-2">/ the audience</div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-tight">Who's in the room.</h2>
          </div>
          <div className="lg:col-span-8 space-y-4 text-white/80 text-lg leading-relaxed">
            <p>Heads of growth, performance marketing leads, paid-media operators, CMOs, and brand directors at category-defining fintechs &mdash; the people who can green-light a partnership at the bar.</p>
            <p className="text-white/60">Verticals covered: Personal loans · Installment &amp; short-term lending · BNPL · Credit-builder tools · Checking &amp; savings apps · Debit &amp; Credit cards · Debt consolidation &amp; refinance · Insurtech · Compliance · Anti-fraud · Data validation.</p>
          </div>
        </div>
      </section>

      {/* TIERS */}
      <section className="border-b border-cyber-line">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 sm:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 mb-12">
            <div className="lg:col-span-4">
              <div className="term-label mb-2">/ tiers</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-tight">Three tiers. <br />Pick your altitude.</h2>
            </div>
            <div className="lg:col-span-8 text-white/65 text-lg">
              All inclusions are per event. We work with each sponsor to make sure your investment matches the room you're walking into.
              Pricing is sent privately after a 15-minute alignment call.
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            {TIERS.map((t) => {
              const Icon = t.icon;
              return (
                <div
                  key={t.id}
                  className={`group hard-border bg-cyber-surface p-7 sm:p-8 flex flex-col relative ${t.id === "platinum" ? "lg:scale-[1.02] lg:z-10 ring-1 ring-cyber-yellow" : ""}`}
                  data-testid={`tier-card-${t.id}`}
                >
                  <span className={`absolute -top-3 left-7 px-3 py-1 font-mono-tech text-[10px] tracking-[0.25em] uppercase ${t.badge}`}>/ {t.label}</span>
                  <div className="flex items-start justify-between mt-4">
                    <Icon size={32} weight="duotone" className={t.accent.split(" ").pop()} />
                    <span className={`font-mono-tech text-[10px] tracking-[0.2em] uppercase border px-2 py-1 ${t.accent}`}>{t.seats}</span>
                  </div>
                  <h3 className="font-display font-black text-3xl mt-6 leading-tight">{t.label}</h3>
                  <p className="text-white/70 text-sm mt-3 leading-relaxed">{t.pitch}</p>
                  <ul className="mt-6 space-y-3 text-white/85 text-sm leading-relaxed flex-1">
                    {t.bullets.map((b, i) => (
                      <li key={i} className="flex items-start gap-3">
                        <CheckCircle size={16} weight="duotone" className={`shrink-0 mt-0.5 ${t.accent.split(" ").pop()}`} />
                        <span>{b}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={`/contact?type=sponsorship&tier=${t.id}`}
                    className="mt-8 btn-neon w-full justify-center"
                    data-testid={`tier-cta-${t.id}`}
                  >
                    {t.cta} <ArrowUpRight size={14} weight="bold" />
                  </Link>
                </div>
              );
            })}
          </div>

          <p className="mt-10 font-mono-tech text-[11px] uppercase tracking-[0.2em] text-white/40">
            / custom packages — multi-event, exclusive category sponsorship, and co-branded city activations are also available. Ask.
          </p>
        </div>
      </section>

      {/* FAQ-LITE */}
      <section className="border-b border-cyber-line bg-cyber-black">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-20 grid grid-cols-1 lg:grid-cols-12 gap-8">
          <div className="lg:col-span-4">
            <div className="term-label mb-2">/ heads-up</div>
            <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl leading-tight">Three things sponsors usually ask.</h2>
          </div>
          <div className="lg:col-span-8 space-y-6">
            <div className="hard-border p-5 bg-cyber-surface">
              <div className="font-display font-black text-xl">Will I be able to actually meet decision-makers?</div>
              <p className="text-white/70 text-sm mt-2 leading-relaxed">Yes. Every attendee is hand-reviewed against your category. We share an anonymised pre-event read of who's coming and a session map so you walk in with a target list.</p>
            </div>
            <div className="hard-border p-5 bg-cyber-surface">
              <div className="font-display font-black text-xl">Do I have to pitch from a stage?</div>
              <p className="text-white/70 text-sm mt-2 leading-relaxed">No stage. No keynote slot. The whole point is that operators talk to operators &mdash; if you want a moment to address the room (Platinum only) it's 60 seconds, no slides, mic-drop format.</p>
            </div>
            <div className="hard-border p-5 bg-cyber-surface">
              <div className="font-display font-black text-xl">Can I get the attendee list?</div>
              <p className="text-white/70 text-sm mt-2 leading-relaxed">We never sell or hand over attendee data. Platinum sponsors get a post-event opt-in directory of the people who explicitly said "you can connect us with sponsor X." That's it &mdash; quality over noise.</p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-cyber-yellow text-cyber-black">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8 py-16 sm:py-20 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6">
          <div className="flex items-center gap-4">
            <Megaphone size={32} weight="duotone" />
            <div>
              <div className="font-mono-tech text-xs uppercase tracking-[0.2em]">/ next step</div>
              <h2 className="font-display font-black tracking-tighter text-4xl sm:text-5xl mt-1 leading-none">Get the sponsor brief.</h2>
            </div>
          </div>
          <Link
            to="/contact?type=sponsorship"
            className="bg-cyber-black text-cyber-yellow border border-cyber-black hover:bg-cyber-black/85 px-7 py-4 font-mono-tech tracking-[0.12em] uppercase font-bold text-sm transition-colors"
            data-testid="sponsor-cta-bottom"
          >
            Request the deck →
          </Link>
        </div>
      </section>
    </main>
  );
};

export default Sponsor;
